import React from "react";

import { cx, useTranslation } from "utils";

import { SOILSIGHT_STEPS } from "consts";

import { Button, Icon, Svg, Typography } from "components";

type PreLoginSoilSightFlowProps = {
  stepIndex: number;
  onClose: () => void;
  onBack: () => void;
  onPrimaryCta: () => void;
  isPrimaryCtaDisabled?: boolean;
  isPrimaryCtaLoading?: boolean;
  showPrimaryCta?: boolean;
  // TODO(KH-1131): remove with the skip-acquisition-flow flag.
  showStepper?: boolean;
  children: React.ReactNode;
};

export const PreLoginSoilSightFlow = ({
  stepIndex,
  onClose,
  onBack,
  onPrimaryCta,
  isPrimaryCtaDisabled = false,
  isPrimaryCtaLoading = false,
  showPrimaryCta = true,
  showStepper = true,
  children,
}: PreLoginSoilSightFlowProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <div className="soilsight-simulation-shell">
      <header className="soilsight-simulation-shell__chrome">
        <Svg svgName="logos/klim_logo_text_black" className="soilsight-simulation-shell__brand-logo" width={75} height={32} />
        <div className="soilsight-simulation-shell__chrome-title">
          <span className="soilsight-simulation-shell__product-badge">{t("preLogin.landing.hero.eyebrow")}</span>
          <Typography variant="h3" color="white" className="soilsight-simulation-shell__chrome-headline">
            {t("preLogin.landing.hero.title")}
          </Typography>
        </div>
        <button
          type="button"
          className="soilsight-simulation-shell__icon-btn soilsight-simulation-shell__icon-btn--on-green soilsight-simulation-shell__icon-btn--close"
          aria-label={t("preLogin.closeLabel")}
          data-testid="soilsight-step-close"
          onClick={onClose}
        >
          <Icon iconName="tabler/x" size="m" color="white" />
        </button>
      </header>

      <div className="soilsight-simulation-shell__card">
        <div className="soilsight-simulation-shell__card-topbar">
          {stepIndex > 0 ? (
            <button type="button" className="soilsight-simulation-shell__icon-btn" aria-label={t("preLogin.simulation.backLabel")} onClick={onBack}>
              <Icon iconName="tabler/arrow-left" size="m" />
            </button>
          ) : null}

          {showStepper ? (
            <ol className="soilsight-simulation-shell__stepper" aria-label={t("preLogin.simulation.stepperLabel")}>
              {SOILSIGHT_STEPS.map((stepKey, stepListIndex) => {
                const isCurrent = stepListIndex === stepIndex;
                const isComplete = stepListIndex < stepIndex;
                return (
                  <React.Fragment key={stepKey}>
                    {stepListIndex > 0 ? <li className="soilsight-simulation-shell__step-divider" aria-hidden="true" /> : null}
                    <li
                      className={cx(
                        "soilsight-simulation-shell__step",
                        isCurrent && "soilsight-simulation-shell__step--current",
                        isComplete && "soilsight-simulation-shell__step--complete"
                      )}
                      aria-current={isCurrent ? "step" : undefined}
                    >
                      <span
                        className={cx(
                          "soilsight-simulation-shell__step-index",
                          isCurrent && "soilsight-simulation-shell__step-index--current",
                          isComplete && "soilsight-simulation-shell__step-index--complete"
                        )}
                        aria-hidden="true"
                      >
                        {isComplete ? <Icon iconName="tabler/check" size="xs" /> : stepListIndex + 1}
                      </span>
                      <span className="soilsight-simulation-shell__step-label">{t(`preLogin.simulation.stepper.${stepKey}`)}</span>
                    </li>
                  </React.Fragment>
                );
              })}
            </ol>
          ) : null}
        </div>

        <div className="soilsight-simulation-shell__card-body">{children}</div>

        <div
          className={cx(
            "soilsight-simulation-shell__card-footer",
            !showPrimaryCta && "soilsight-simulation-shell__card-footer--hidden",
            SOILSIGHT_STEPS[stepIndex] === "rotation" && "soilsight-simulation-shell__card-footer--rotation"
          )}
        >
          {showPrimaryCta ? (
            <Button
              type="primary"
              dataTestId="soilsight-step-advance"
              className="soilsight-simulation-shell__cta"
              disabled={isPrimaryCtaDisabled}
              loading={isPrimaryCtaLoading}
              onClick={onPrimaryCta}
            >
              {t(`preLogin.simulation.cta.${SOILSIGHT_STEPS[stepIndex]}`)}
            </Button>
          ) : null}
        </div>
      </div>

      <footer className="soilsight-simulation-shell__trust">
        <Typography variant="paragraph-m" color="white" bold>
          {t("preLogin.simulation.trust")}
        </Typography>
      </footer>
    </div>
  );
};
